/**
 * ================================================================================================
 *  A HOME WIDGET, AND THE FOUR REASONS IT MIGHT HAVE NOTHING TO SHOW
 * ================================================================================================
 *
 * Owner section 4, 2026-10-28, verbatim:
 *
 *     *"לכל ווידג׳ט ציין מה מחובר בפועל, מה מקור הנתונים ומה חסר. חבר מקורות קיימים, והפרד בין:
 *     אין נתונים · אין הרשאה · לא הוגדר חיבור · הרכיב טרם מומש. אין להציג אפס כנתון אמיתי כשאין
 *     מקור נתונים."*
 *
 * ── WHY THE FOUR HAVE TO BE FOUR ────────────────────────────────────────────────────────────────
 *
 * They look alike on screen and they are completely different facts, each with a different person
 * who can act on it:
 *
 *   **אין נתונים** — the read succeeded and the office genuinely has none. Nobody is at fault and
 *                    there may be nothing to do. This is the only one of the four that is a
 *                    measurement, and it is the only one a figure of `0` would be honest about.
 *   **אין הרשאה**  — this person may not see it. Somebody else can; the office manager grants it.
 *   **לא הוגדר חיבור** — the capability exists and this office has not set it up yet. The office
 *                    can fix it, and the tile says where.
 *   **טרם מומש**   — nobody can see it, in any office, in this release. There is nothing to do and
 *                    the product says so instead of implying a setting is missing.
 *
 * Collapsing them is what produces the two failures the Owner has named twice: a zero that is not a
 * measurement, and a tile that claims to be waiting for a connection when the component behind it
 * was never built.
 *
 * ── A FIGURE COMES FROM A READ, NEVER FROM A DEFAULT ────────────────────────────────────────────
 *
 * This component renders no numbers of its own. A measured tile puts `<ServerRead>` in `children`,
 * so loading, failure and refusal keep the renderings they already have across the product, and the
 * figure cannot exist unless a read produced it — the `capacityPanelHasARead` lesson, one screen
 * over. An absence is stated through `absence`, which has no arm for "a number I happen to have".
 *
 * `data-widget-state` carries the answer so a test can assert that the four are told apart in the
 * markup rather than in the copy.
 */

import type { ReactNode } from 'react';

import { Icon, type IconName } from '../shell/Icon';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';

/** The three absences that are not a measurement. `no_data` lives INSIDE a successful read. */
export type WidgetAbsence =
  | { readonly kind: 'not_permitted' }
  | {
      readonly kind: 'not_connected';
      /** Where the office goes to set it up. Given only when the office can actually do it. */
      readonly action?: { readonly href: string; readonly labelHe: string };
    }
  | {
      readonly kind: 'not_implemented';
      /** The widget's own reason, where it has one more specific than the generic sentence. */
      readonly reasonHe?: string;
    };

export interface WidgetPanelProps {
  readonly titleHe: string;
  readonly icon: IconName;
  /**
   * WHERE THE TILE OPENS, and it is given ONLY to a tile holding something measured.
   *
   * The Owner's rule has two halves and both are kept: *"an active widget opens its screen, and an
   * inert one does not pretend to."* A link out of a tile that says "not implemented" leads to a
   * screen that says the same thing in more words.
   */
  readonly href?: string;
  /** Stated instead of a figure. Absent ⇒ `children` must produce the figure from a read. */
  readonly absence?: WidgetAbsence;
  /**
   * WHICH READING THIS CARD IS, for the stylesheet. **2026-11-01.**
   *
   * One rule needs it: the shekel symbol is implied by a card headed `שערי מטבע` and is
   * implied by nothing else, so the narrow rail hides it there and only there. It is the
   * server's own `domainCode`, passed through rather than re-derived from the title.
   */
  readonly domain?: string;
  readonly children?: ReactNode;
}

export function WidgetPanel({
  titleHe,
  icon,
  href,
  absence,
  domain,
  children,
}: WidgetPanelProps): JSX.Element {
  const { t } = useLocale();

  const badgeHe =
    absence === undefined
      ? null
      : absence.kind === 'not_permitted'
        ? t('state.notPermitted')
        : absence.kind === 'not_connected'
          ? t('state.configurationRequired')
          : t('state.notImplemented');

  const bodyHe =
    absence === undefined
      ? null
      : absence.kind === 'not_permitted'
        ? t('state.notPermittedBody')
        : absence.kind === 'not_connected'
          ? t('state.configurationRequiredBody')
          : (absence.reasonHe ?? t('state.notImplementedBody'));

  return (
    <section
      className={`gp-panel${absence === undefined && href !== undefined ? ' gp-panel--linked' : ''}`}
      data-widget-state={absence?.kind ?? 'measured'}
      /*
        WHICH READING THIS CARD IS. The stylesheet needs it for one rule: the shekel symbol is
        implied by a card headed `שערי מטבע` and is not implied by anything else, so the rail
        hides it there and only there. A class per domain would have been the alternative and
        would have put a list of domains in two files.
      */
      {...(domain === undefined ? {} : { 'data-widget-domain': domain })}
    >
      <div className="gp-section-heading">
        <h2>
          <span className="gp-overview-icon">
            <Icon name={icon} />
          </span>
          {/* A LINK ONLY WHERE THERE IS SOMETHING TO OPEN. See `href` above. */}
          {absence === undefined && href !== undefined ? (
            <a className="gp-w-widget-link" href={href}>
              {titleHe}
            </a>
          ) : (
            titleHe
          )}
        </h2>
        {badgeHe === null ? null : <span className="gp-badge gp-plan">{badgeHe}</span>}
      </div>

      {bodyHe === null ? null : <p className="gp-muted gp-small">{bodyHe}</p>}

      {absence !== undefined && absence.kind === 'not_connected' && absence.action !== undefined ? (
        <p className="gp-w-planned-action">
          <a className="btn btn--nav" href={absence.action.href}>
            {absence.action.labelHe}
          </a>
        </p>
      ) : null}

      {children}
    </section>
  );
}

/**
 * A MEASURED FIGURE. `dir="ltr"` because a number inside RTL text is controlled, never inferred
 * (09 §9.5 A1), and `unitHe` is the word that makes a bare count readable.
 */
export function WidgetFigure({
  value,
  unitHe,
}: {
  readonly value: number;
  readonly unitHe?: string;
}): JSX.Element {
  return (
    <>
      <p className="gp-w-figure" dir="ltr">
        {value}
      </p>
      {unitHe === undefined ? null : <p className="gp-muted gp-small">{unitHe}</p>}
    </>
  );
}

/**
 * ONE READING, AS A READABLE ROW: NAME · VALUE · UNIT. **Owner, section 5, 2026-11-01.**
 *
 *     *"סדר את התצוגה לשורות קריאות של שם, ערך ויחידה. תקן הצמדת קודי מטבע למספרים, ערבוב
 *     כיווניות."*
 *
 * ── THE THREE PARTS ARE THREE ELEMENTS, WHICH IS WHAT FIXES THE DIRECTIONALITY ──────────────────
 *
 * The row this replaces put the name and the value in two spans with the value marked `dir="ltr"`
 * and the unit CONCATENATED into the number. In an RTL paragraph that produces exactly what the
 * Owner reported: a currency code welded to its figure, and a line whose pieces reorder depending
 * on whether the value happens to start with a digit or a sign.
 *
 * Here the name, the value and the unit are three boxes in a flex row. A flex container lays its
 * children out by the WRITING DIRECTION OF THE CONTAINER, not by the characters inside them, so
 * the order is fixed by the layout and cannot be rearranged by the bidi algorithm. The value is
 * its own `dir="ltr"` box — a number is always read left to right (09 §9.5 A1) — and the unit is
 * a separate box with a gap, so nothing is ever glued to a figure.
 *
 * ── AND A CARD DOES NOT SAY ITS OWN NAME TWICE ─────────────────────────────────────────────────
 *
 * `nameHe` is OPTIONAL. A card with one reading whose series has no name of its own — the policy
 * rate is the example — would otherwise render *"ריביות והצמדה · ריביות והצמדה · 4.25 · %"*,
 * because the fallback for an unnamed series is the card's own heading and the heading is already
 * above it. Without a name the row is the card's FIGURE, and it is set larger to say so.
 */
export function WidgetReading({
  nameHe,
  code,
  value,
  unitHe,
  changePercent,
}: {
  readonly nameHe?: string;
  /**
   * THE PUBLISHER'S OWN SHORT CODE, beside the name and not instead of it. **Section 2.**
   *
   * *"שם המטבע והקוד שלו."* `USD` alone is a row an accountant reads fluently and a bookkeeper
   * does not; `דולר ארה"ב` alone loses the token that appears on every bank statement and in
   * every accounting system. Both, with the code quiet.
   */
  readonly code?: string;
  readonly value: string;
  readonly unitHe?: string;
  /**
   * THE PUBLISHER'S OWN STATEMENT OF CHANGE, or nothing at all. **Section 3.**
   *
   *     *"שינוי באחוזים רק כשיש נתון אמיתי. אין להמציא שינוי או להסיק אותו מאפס."*
   *
   * `undefined` renders NOTHING — not a dash, not `0%`, and nothing in the colour column. A zero
   * is a measurement: it means the publisher said this reading did not move, and it renders
   * neutral, which is a third state and not the absence of the other two.
   */
  readonly changePercent?: string;
}): JSX.Element {
  const direction = changeDirection(changePercent);
  return (
    <div className="gp-w-reading" data-solo={nameHe === undefined ? 'yes' : undefined}>
      {nameHe === undefined ? null : (
        /*
          `title` BECAUSE THE RAIL CLIPS IT. The narrow card shortens a long name with an
          ellipsis so every row is one line; the full text stays in the DOM, so a screen reader
          reads all of it, and a pointer can see all of it. Nothing is hidden from anybody who
          asks.
        */
        <span className="gp-w-reading__name" title={nameHe}>
          {nameHe}
        </span>
      )}
      {code === undefined ? null : (
        <span className="gp-w-reading__code" dir="ltr">
          {code}
        </span>
      )}
      <span className="gp-w-reading__value" dir="ltr">
        {value}
      </span>
      {unitHe === undefined ? null : <span className="gp-w-reading__unit">{unitHe}</span>}
      {direction === null || changePercent === undefined ? null : (
        <ChangeMark direction={direction} percent={changePercent} />
      )}
    </div>
  );
}

/** Up, down, or measured-and-unchanged. `null` means the publisher gave this product no figure. */
export type ChangeDirection = 'up' | 'down' | 'flat';

/**
 * THE SIGN OF A FIGURE THE PUBLISHER SENT, AND `null` FOR EVERYTHING ELSE.
 *
 * It parses rather than trusting the string, because the column is `DECIMAL` on the wire and
 * arrives as text: `'0.0000'`, `'-0.12'`, `'1.5'`. A value that does not parse is treated as no
 * figure, which is the safe direction — the alternative is an arrow pointing somewhere on the
 * strength of a string nobody could read.
 */
export function changeDirection(percent: string | undefined): ChangeDirection | null {
  if (percent === undefined || percent.trim() === '') return null;
  const value = Number(percent);
  if (!Number.isFinite(value)) return null;
  if (value > 0) return 'up';
  if (value < 0) return 'down';
  return 'flat';
}

/**
 * ── COLOUR AND SHAPE, NOT COLOUR ALONE. **Owner, section 3.** ─────────────────────────────────
 *
 *     *"עלייה וירידה בצבע ובחץ."*
 *
 * WCAG 1.4.1 says the same thing for a different reason: about one man in twelve cannot separate
 * this product's green from its red, and a column where the only difference is the hue is a
 * column they read as a list of unsigned numbers. The arrow is the primary mark and the colour
 * reinforces it.
 *
 * THE WORD IS FOR A SCREEN READER, because neither the arrow nor the colour reaches one. `▲` is
 * announced as *"black up-pointing triangle"* by some and skipped entirely by others, so the
 * glyph is `aria-hidden` and a real word stands beside it.
 */
const CHANGE_WORD: Readonly<Record<ChangeDirection, TextKey>> = {
  up: 'market.change.up',
  down: 'market.change.down',
  flat: 'market.change.flat',
};

const CHANGE_GLYPH: Readonly<Record<ChangeDirection, string>> = {
  up: '▲',
  down: '▼',
  /* An EQUALS SIGN and not a dash: a dash in a column of figures reads as "no data", which is
     the one thing this state is not. */
  flat: '=',
};

function ChangeMark({
  direction,
  percent,
}: {
  readonly direction: ChangeDirection;
  readonly percent: string;
}): JSX.Element {
  const { t } = useLocale();
  /*
   * THE SIGN IS THE ARROW'S JOB, so the number is printed without one: `▼ 0.12%` and never
   * `▼ -0.12%`, which reads as a double negative. `Math.abs` is applied to the PARSED value and
   * the result is re-formatted to the publisher's own precision -- stripping a leading `-` from
   * the string would leave `+0.12` on a source that writes its sign.
   */
  const parsed = Number(percent);
  /*
   * TWO PLACES, ALWAYS. **Owner's table, 2026-11-01:** `0.50%` and `1.60%`, not `0.5%` and
   * `1.6%`. A column of changes is read by comparing them, and a ragged number of decimals is
   * what makes a column need reading twice. The publishers agree: the Bank of Israel sends two,
   * the ministry's calculator prints two, and the CBS releases two.
   */
  const magnitude = Number.isFinite(parsed) ? Math.abs(parsed).toFixed(2) : percent;
  return (
    <span className="gp-w-reading__change" data-direction={direction}>
      <span aria-hidden="true">{CHANGE_GLYPH[direction]}</span>
      <span className="visually-hidden">{t(CHANGE_WORD[direction])}</span>
      <span dir="ltr">{magnitude}%</span>
    </span>
  );
}

/**
 * WHAT A 404 MEANS ON A LIST READ. **Section 6, 2026-11-01.**
 *
 * Handed to `<ServerRead renderNotFound=…>` by every widget on the home screen, so the three of
 * them cannot drift into three wordings of one fact. `catalogue.ts` carries the reasoning; the
 * short version is that the generic 404 sentence is written for an ITEM route and says *"no change
 * was made to it"* about a read that was changing nothing.
 */
export function WidgetReadFault(): JSX.Element {
  const { t } = useLocale();
  return <p className="gp-w-empty" data-read-fault="not-found">{t('read.notFoundOnServer')}</p>;
}

/**
 * THE READ SUCCEEDED AND THE ANSWER IS NONE — the one absence that is a measurement.
 *
 * Rendered from inside `<ServerRead>`'s loaded branch, which is what makes it a fact about the
 * office rather than about the request. *"אין להציג אפס במקום לא התקבלו נתונים"* and its converse:
 * this is the case where there is no figure to show BECAUSE there is nothing, and it says so in
 * words rather than printing a zero that reads as all four states at once.
 */
export function WidgetNone({ bodyHe }: { readonly bodyHe?: string }): JSX.Element {
  const { t } = useLocale();
  return <p className="gp-w-empty">{bodyHe ?? t('state.noDataYet')}</p>;
}
